// src/components/AmountDropdown.tsx

import React, { useState, useRef, useEffect } from 'react';
import '../css/amountdropdown.css'; // Asegúrate de crear este archivo para los estilos

interface AmountDropdownProps {
  desde: string;
  hasta: string;
  onChange: (desde: string, hasta: string) => void;
  placeholder?: string;
}

const AmountDropdown: React.FC<AmountDropdownProps> = ({
  desde,
  hasta,
  onChange,
  placeholder = 'Importe',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localDesde, setLocalDesde] = useState(desde);
  const [localHasta, setLocalHasta] = useState(hasta);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string>('');

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Sincronizar localDesde con la prop 'desde'
  useEffect(() => {
    setLocalDesde(desde);
  }, [desde]);

  // Sincronizar localHasta con la prop 'hasta'
  useEffect(() => {
    setLocalHasta(hasta);
  }, [hasta]);

  // Validar que "Desde" no sea mayor que "Hasta"
  useEffect(() => {
    if (localDesde && localHasta) {
      const desdeNum = parseInt(localDesde, 10);
      const hastaNum = parseInt(localHasta, 10);
      if (!isNaN(desdeNum) && !isNaN(hastaNum) && desdeNum > hastaNum) {
        setError('"Desde" no puede ser mayor que "Hasta".');
      } else {
        setError('');
      }
    } else {
      setError('');
    }
  }, [localDesde, localHasta]);

  // Actualizar el estado padre cuando localDesde o localHasta cambien y no haya errores
  useEffect(() => {
    if (!error) {
      onChange(localDesde, localHasta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDesde, localHasta, error]);

  // Formatear los números como "10.000€"
  const formatAmount = (amount: string) => {
    if (!amount) return '';
    const number = parseInt(amount, 10);
    if (isNaN(number)) return '';
    return number.toLocaleString('es-ES') + '€';
  };

  const displayText =
    localDesde && localHasta
      ? `${formatAmount(localDesde)} - ${formatAmount(localHasta)}`
      : localDesde
      ? `Desde ${formatAmount(localDesde)}`
      : localHasta
      ? `Hasta ${formatAmount(localHasta)}`
      : placeholder;

  return (
    <div className="amount-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown-toggle ${error ? 'dropdown-toggle-error' : ''}`}
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {displayText} <span className="arrow">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          <div className="amount-inputs">
            <div className="input-group">
              <label htmlFor="desde">Desde:</label>
              <input
                type="number"
                id="desde"
                value={localDesde}
                onChange={(e) => setLocalDesde(e.target.value)}
                placeholder="0"
                min="0"
              />
            </div>
            <div className="input-group">
              <label htmlFor="hasta">Hasta:</label>
              <input
                type="number"
                id="hasta"
                value={localHasta}
                onChange={(e) => setLocalHasta(e.target.value)}
                placeholder="0"
                min="0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmountDropdown;
